import { useEffect, useRef } from "react"

import { INTERNAL_EVENTS } from "@/const/event.constants"
import { useTranslation } from "@/hooks/useTranslation"
import { DatePicker } from "@/organisms/SmartSearch/components/molecules/DatePicker"
import {
  CONTAINER_SELECTOR,
  FILTER_ATTRIBUTE_NAME
} from "@/templates/TabTemplate"
import { DropdownSmartSearch } from "./components/organisms/DropdownSmartSearch"
import { InputSmartSearch } from "./components/organisms/InputSmartSearch"
import { VulnerabilityAlert } from "./components/organisms/VulnerabilityAlert"
import { createFilterOptions } from "./helpers/option.helpers"
import { useClickOutsideSmartSearch } from "./hooks/useClickOutsideSmartSearch"
import { useSearch } from "./hooks/useSearch"
import { useTooltip } from "./hooks/useTooltip"

import styles from "./SmartSearch.module.scss"

export const SmartSearch = ({
  onlySearch,
  placeholder,
  prefix,
  id = "smartSearch",
  data = {},
  filters = [],
  searchedVulnerabilities
}: any) => {
  const filterOptions = createFilterOptions(filters, data)
  const ref = useRef<HTMLDivElement>(null)
  const refRow = useRef<HTMLDivElement | undefined | null>()
  const props = useSearch({ data, filterOptions, onlySearch, prefix })
  const { t } = useTranslation("translation", {
    keyPrefix: "filter.placeholder"
  })
  const { open, handlers, services } = props

  useClickOutsideSmartSearch(ref, props)
  useTooltip()

  useEffect(() => {
    if (!refRow.current) {
      refRow.current = ref.current?.closest(CONTAINER_SELECTOR)
      services.StringService.setRefFilterCounter(
        refRow.current,
        FILTER_ATTRIBUTE_NAME
      )
    }

    const cb = (e: Event) => handlers.onClear(e, { apply: true, focus: false })

    refRow.current?.addEventListener(
      INTERNAL_EVENTS.CLEAR_FILTERS_TABLE_TAB_TEMPLATE,
      cb
    )

    return () =>
      refRow.current?.removeEventListener(
        INTERNAL_EVENTS.CLEAR_FILTERS_TABLE_TAB_TEMPLATE,
        cb
      )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const defaultPlaceholder = t(onlySearch ? "search" : "select")

  return (
    <>
      <div id={id} ref={ref} className={styles.container}>
        <DropdownSmartSearch
          id={id}
          data={data}
          open={onlySearch ? false : open}
          onClick={handlers.onClick}
          Input={
            <InputSmartSearch
              onlySearch={onlySearch}
              placeholder={placeholder || defaultPlaceholder}
              {...props}
            />
          }
          services={services}
        />
        <DatePicker
          onChange={(nextValue: string, focus: boolean) => {
            props.handlers.onClick({ key: nextValue })

            if (focus) props.handlers.onFocus()
            else props.handlers.onBlur()

            setTimeout(() => {
              props.refInput.current?.focus()
            }, 50)
          }}
          {...props}
        />
      </div>
      <VulnerabilityAlert searchedVulnerabilities={searchedVulnerabilities} />
    </>
  )
}
